import React, { useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

// Using existing images from the project
import villa1 from "../../images/home/vacation1.png";
import villa2 from "../../images/home/vacation2.png";
import villa3 from "../../images/home/vacation3.png";
import villa4 from "../../images/home/vacation4.png";
import villa5 from "../../images/home/vacation5.png";
import villa6 from "../../images/home/vacation6.png";
import cardicon1 from "../../images/aboutUs/cardIcon.png";
import cardicon2 from "../../images/aboutUs/cardIcon2.png";
import cardicon3 from "../../images/aboutUs/cardIcon3.png";
import cardicon4 from "../../images/aboutUs/cardIcon4.png";
import culture1 from "../../images/home/culture1.png";
import culture2 from "../../images/home/culture2.png";
import wildlife1 from "../../images/home/wildlife1.png";
import wildlife2 from "../../images/home/wildlife2.png";
import nature1 from "../../images/home/nature1.png";
import nature2 from "../../images/home/nature2.png";

const ActivityHighlights = () => {
  const observerRef = useRef(null);
  
  useEffect(() => {
    // Initialize Intersection Observer for fade-in animations
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, { threshold: 0.1 });
    
    // Observe all fade-in elements
    const fadeElements = document.querySelectorAll('.fade-in, .staggered-fade');
    fadeElements.forEach(el => {
      observerRef.current.observe(el);
    });
    
    return () => {
      if (observerRef.current) {
        fadeElements.forEach(el => {
          observerRef.current.unobserve(el);
        });
      }
    };
  }, []);

  const activities = [
    // {
    //   title: "Luxurious Living Space",
    //   description: "Unwind in our carefully crafted villa that blends tradition and comfort by using high-end materials to replicate local architecture for a comfortable stay.  Inhabit a space meticulously designed with custom elements that let you experience nature even when you are indoors.",
    //   icon: cardicon1,
    //   primaryImage: villa1,
    //   secondaryImages: [villa2, villa5],
    //   features: ["Private Balcony", "Luxury Amenities", "Designer Interiors"]
    // },
    {
      title: "Wildlife Safari",
      description: "Experience the thrill of spotting the majestic Asiatic lion and other wildlife in their natural habitat during an unforgettable guided safari tour. Our reservoir attracts wildlife, so don’t be surprised if you encounter a deer, peacock, or even a lion near the property.",
      icon: cardicon2,
      primaryImage: wildlife1,
      secondaryImages: [wildlife1, wildlife2],
      features: ["Lion Tracking", "Bird Watching", "Expert Guides"]
    },
    {
      title: "Cultural Performances",
      description: "Discover local traditions and enjoy the vibrant Siddi folk dance performances, which include energetic performances, fire displays, and authentic music. The folk dance is performed on request, upon availability, by the local Siddi community.",
      icon: cardicon3,
      primaryImage: culture1,
      secondaryImages: [culture1,culture2],
      features: ["Traditional Dance", "Local Musicians", "Cultural Experience"]
    },
    {
      title: "Nature Walks",
      description: "Take a walk through the surrounding mango orchards and experience Gir’s diverse ecosystem firsthand. Get guided tours around the property and get to know the biodiversity and natural beauty offered by the region.",
      icon: cardicon4,
      primaryImage: nature1,
      secondaryImages: [nature1, nature2],
      features: ["Guided Tours", "Flora Exploration", "Wildlife Spotting"]
    }
  ];

  return (
    <div className="py-16 px-4 md:px-8 bg-white">
      <div className="max-w-[1440px] mx-auto">
        <div className="text-xl text-[#585858] poppins-medium text-center fade-in">
          UNFORGETTABLE EXPERIENCES
        </div>
        <div className="mt-2 tracking-wide mx-auto flex justify-center text-center md:w-[700px] md:leading-snug text-2xl md:text-[46px] font-bold text-[#672220] fade-in">
          Collect Unforgettable Memories to Take Back Home
        </div>

        {/* Featured Activities - Alternating Layout */}
        <div className="mt-16 space-y-24">
          {activities.map((activity, index) => (
            <div 
              key={index} 
              className={`activity-feature ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} flex flex-col md:flex gap-8 fade-in`}
            >
              {/* Image Section with Slider */}
              <div className="md:w-1/2 relative">
                <div className="scroll-image-container shadow-xl rounded-xl overflow-hidden">
                  <Swiper
                    modules={[Autoplay, EffectFade]}
                    effect="fade"
                    speed={1000}
                    autoplay={{ 
                      delay: 3000,
                      disableOnInteraction: false 
                    }}
                    loop={true}
                    className="activity-swiper h-[450px]"
                  >
                    <SwiperSlide>
                      <img
                        src={activity.primaryImage}
                        alt={activity.title}
                        className="w-full h-full object-cover"
                      />
                    </SwiperSlide>
                    {activity.secondaryImages.map((img, i) => (
                      <SwiperSlide key={i}>
                        <img
                          src={img}
                          alt={`${activity.title} - view ${i+1}`}
                          className="w-full h-full object-cover"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
                
                {/* Icon Badge */}
                {/*<div className="absolute -bottom-6 left-6 w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center z-10">
                  <img src={activity.icon} alt={`${activity.title} icon`} className="w-8 h-8" />
                </div>
                */}
              </div>
              
              {/* Content Section */}
              <div className="md:w-1/2 flex flex-col justify-center mt-10 md:mt-0 staggered-fade">
                <div className="activity-indicator"></div>
                <h3 className="text-3xl text-[#672220] font-bold mb-4">{activity.title}</h3>
                <p className="text-[#5A5A5A] text-lg mb-6">
                  {activity.description}
                </p>
                
                {/* Features */}
                <div className="flex flex-wrap gap-3 mb-6">
                  {activity.features.map((feature, i) => (
                    <span 
                      key={i} 
                      className="bg-[#D1A752] bg-opacity-10 text-[#672220] px-4 py-2 rounded-full text-sm"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
                
                {/* Small Gallery Preview */}
                <div className="hidden md:flex gap-3 mt-2">
                  {activity.secondaryImages.map((img, i) => (
                    <div key={i} className="w-16 h-16 overflow-hidden rounded-md">
                      <img 
                        src={img} 
                        alt={`Preview ${i+1}`} 
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Testimonial/Quote section */}
        <div className="mt-24 text-center fade-in">
          <blockquote className="text-2xl md:text-3xl text-[#5A5A5A] font-light italic max-w-[900px] mx-auto">
            "The purpose of life is to live it, to taste experience to the utmost, to reach out eagerly and without fear for newer and richer experience."
          </blockquote>
          <p className="mt-4 text-[#672220] font-medium">- Eleanor Roosevelt</p>
        </div>
      </div>
    </div>
  );
};

export default ActivityHighlights;